import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "../lib/utils";

/* ----------------------------- Scroll reveal ----------------------------- */
export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ----------------- Kinetic masked line-by-line hero reveal ---------------- */
export const KineticLines = ({ lines, className = "", lineClass = "", start = 0.15 }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden py-[0.06em]">
        <motion.span
          className={cn("block", lineClass)}
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 0.95, delay: start + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

/* -------------------------------- Buttons -------------------------------- */
export const GradientButton = ({ to, href, children, className = "", icon = true, ...props }) => {
  const inner = (
    <span className="relative z-10 inline-flex items-center gap-2">
      {children}
      {icon && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
    </span>
  );
  const cls = cn(
    "group relative inline-flex items-center justify-center rounded-full bg-brand-gradient px-7 py-3.5 text-sm font-semibold text-white",
    "shadow-[0_10px_40px_-12px_rgba(255,90,54,0.7)] transition-transform duration-300 hover:-translate-y-0.5",
    "focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-ink",
    className
  );
  if (to) return <Link to={to} className={cls} {...props}>{inner}</Link>;
  return <a href={href} className={cls} {...props}>{inner}</a>;
};

export const GhostButton = ({ to, href, children, className = "", ...props }) => {
  const cls = cn(
    "group inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white",
    "transition-colors duration-300 hover:bg-white/5 hover:border-white/40",
    "focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-ink",
    className
  );
  if (to) return <Link to={to} className={cls} {...props}>{children}</Link>;
  return <a href={href} className={cls} {...props}>{children}</a>;
};

/* ------------------------------ Section head ----------------------------- */
export const Eyebrow = ({ children, className = "" }) => (
  <span className={cn("inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] text-brand-gold", className)}>
    <span className="h-1.5 w-1.5 rounded-full bg-brand" />
    {children}
  </span>
);

export const SectionHeading = ({ eyebrow, title, sub, align = "left", className = "" }) => (
  <div className={cn(align === "center" ? "mx-auto max-w-2xl text-center" : "max-w-2xl", className)}>
    {eyebrow && <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>}
    <Reveal delay={0.05}>
      <h2 className="mt-5 text-3xl font-semibold leading-[1.1] sm:text-4xl lg:text-5xl">{title}</h2>
    </Reveal>
    {sub && (
      <Reveal delay={0.1}>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">{sub}</p>
      </Reveal>
    )}
  </div>
);

/* -------------------------------- Glow orb -------------------------------- */
export const GlowOrb = ({ className = "", color = "#FF5A36" }) => (
  <div
    aria-hidden
    className={cn("pointer-events-none absolute rounded-full blur-[120px] animate-glow-pulse", className)}
    style={{ backgroundColor: color }}
  />
);
